import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { MessageSquare, Send, Paperclip, Smile, FileIcon, X, Plus, Volume2, Play, MoreVertical, Share2, Bookmark, SmilePlus, Hand, Eye, Check, Reply, Pencil, Copy } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { ViewTitle } from "@/components/ui/view-title";
import type { Channel } from "@/utils/custom-types";
import { useInfiniteQuery, useMutation, useQuery } from "@tanstack/react-query";
import { queryClient, trpc } from "@/utils/trpc";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import { email, file, string } from "zod";
import { messages } from "../../../../server/src/message/message-schema";
import { clearChatDraft, getChatDraft, setChatDraft } from "@/contexts/chat-draft-store";
import { Textarea } from "@/components/ui/textarea";
import { ResizableHandle, ResizablePanel, ResizablePanelGroup } from "../ui/resizable";
import { ChatAudioItem, ChatFileItem, ChatMessageItem, ChatThread } from "@/layout/components/chat-thread";
import { handleDropInput, handlePasteAttachments, linkifyText, mimeFromExt, type PendingAttachment } from "@/layout/components/chat-helpers";
import { message } from "@univerjs/presets";
import { useChannelLinkHandler } from "@/hooks/use-channel-link-handler";

export interface ChatViewProps {
	workspaceId: string;
	channelId?: string;
	channel?: Channel | null;
	session?: any;
	currentWorkspace?: any;
}

export const ChatView: React.FC<ChatViewProps> = ({
	workspaceId,
	channelId,
	channel,
	session,
	currentWorkspace,
}) => {
	const { handleLinkClick } = useChannelLinkHandler();
	const channelType = channel?.type ?? "message-group";
	const validChannel = ["message-group", "selfdm", "self-dm", "dm"].includes(channelType);

	const fetchMessages = async ({ pageParam }: { pageParam?: number }) => {
		return queryClient.fetchQuery({
			...trpc.message.getByChannel.queryOptions({
				channelId: Number(channelId),
				limit: 50,
				cursor: pageParam,
			}),
			staleTime: 0
		})
	}

	const messagesQueryKey = trpc.message.getByChannel.queryKey({
		channelId: Number(channelId)
	});

	const channelsQueryKey = trpc.channel.getByUserAndWorkspace.queryKey({
		workspaceId: Number(workspaceId),
	});

	const [dividerAnchorId, setDividerAnchorId] = useState<number | null>(null);
	const [windowFocused, setWindowFocused] = useState(
		typeof document !== "undefined" ? document.hasFocus() : true
	);
	const [isAtBottom, setIsAtBottom] = useState(false);
	const prevChannelIdRef = useRef<string | null>(null);
	const prevLatestMessageIdRef = useRef<number | null>(null);
	const hasPinnedNewRef = useRef<boolean>(false);
	const setHasPinnedNew = (next: boolean, reason: string, extra?: Record<string, unknown>) => {
		hasPinnedNewRef.current = next;
	};

	const { 
		data: messagesInfinite,
		fetchNextPage,
		hasNextPage,
		isFetchingNextPage,
		isLoading,
		isFetching
	} = useInfiniteQuery({
		queryKey: messagesQueryKey,
		queryFn: fetchMessages,
		initialPageParam: undefined as number | undefined,
		getNextPageParam: (lastPage) => (lastPage.hasMore ? lastPage.nextCursor : undefined),
		enabled: validChannel,
		refetchOnMount: "always",
	})

	const isSubscriptionSeeded = Boolean((messagesInfinite as any)?.__subscriptionSeeded);
	const showInitialLoading = isLoading || (isSubscriptionSeeded && isFetching && !isFetchingNextPage);

	const messagesData = useMemo(() => {
		const pages = messagesInfinite?.pages ?? [];
		const messages = pages
			.slice()
			.reverse()             
			.flatMap((p) => p.messages ?? []);

		const last = pages[pages.length - 1];

		return {
			messages,
			hasMore: last?.hasMore ?? false,
			nextCursor: last?.nextCursor,
		};
	}, [messagesInfinite])

	const initialLastReadMessageId = messagesInfinite?.pages?.[0]?.lastReadMessageId;
	const bootstrapLastReadMessageId =
		typeof initialLastReadMessageId === "undefined"
			? channel?.lastReadMessageId ?? null
			: initialLastReadMessageId ?? null;

	useEffect(() => {
		if (prevChannelIdRef.current !== channelId) {
			const next = bootstrapLastReadMessageId ?? null;
			setDividerAnchorId(next);
			prevChannelIdRef.current = channelId ?? null;
			prevLatestMessageIdRef.current = null;
		}
	}, [bootstrapLastReadMessageId, channelId]);

	useEffect(() => {
		const onFocus = () => setWindowFocused(true);
		const onBlur = () => setWindowFocused(false);

		window.addEventListener("focus", onFocus);
		window.addEventListener("blur", onBlur);

		return () => {
			window.removeEventListener("focus", onFocus);
			window.removeEventListener("blur", onBlur);
		};
	}, []);

	const deleteMessageMutation = useMutation(
		trpc.message.delete.mutationOptions()
	);

	const updateMessageMutation = useMutation(
		trpc.message.update.mutationOptions()
	);

const createMessageMutation = useMutation(
	trpc.message.create.mutationOptions({
		onSuccess: (data, variables) => {
			const tempId = variables.tempId;

			// Move divider anchor first so it disappears before the message re-render.
			setDividerAnchorId(data.id);
			if (!markChannelReadMutation.isPending) {
				markChannelReadMutation.mutate({ channelId: Number(channelId) });
			}

			queryClient.setQueryData(messagesQueryKey, (prev: any) => {
				// console.log("[createMessageMutation.onSuccess] data:", JSON.stringify(data, null, 2));
				if (!prev?.pages?.length) return prev;
				// const serverAttachments = data?.attachments ?? [];
		
					const pages = prev.pages.map((page: any) => {
						const msgs = page.messages ?? [];
						const idx = msgs.findIndex((m: any) => m.tempId === tempId);
						if (idx === -1) return page;
		
						const prevMsg = msgs[idx];
						// prevMsg.attachments?.forEach((a: any) => {
						// 	if (a.url?.startsWith("blob:")) {
						// 		URL.revokeObjectURL(a.url);
						// 	}
						// });

						  
						const next = [...msgs];
						// console.log("Replacing optimistic message", 
						// 	{ 
						// 		old: prevMsg, 
						// 		new: {
						// 			...next[idx],
						// 			id: data.id,
						// 			sending: false,
						// 		}
						// 	}
						// );

						next[idx] = {
							...next[idx],
							id: data.id,
							sending: false,
							// attachments: serverAttachments,
						};

						return { ...page, messages: next };
					});
			
					return { ...prev, pages };
				});
			}
		}),
	);

	// const createTestMessageMutation = useMutation(
	// 	trpc.message.createTest.mutationOptions({
	// 		onError: (error) => {
	// 			toast.error(error.message || "Failed to create test message");
	// 		},
	// 	}),
	// );

	const createPresignedFileUploadMutation = useMutation(
		trpc.message.createPresignedFileUpload.mutationOptions(),
	);

const markChannelReadMutation = useMutation(
	trpc.message.markChannelRead.mutationOptions({
		onSuccess: (data) => {
			queryClient.setQueryData(messagesQueryKey, (prev: any) => {
				if (!prev?.pages) return prev;
				const lastReadAt = data?.lastReadAt ?? new Date().toISOString();
				return {
					...prev,
					pages: prev.pages.map((p: any) => ({
						...p,
						lastReadMessageId: data?.lastReadMessageId ?? p.lastReadMessageId,
						lastReadAt,
					})),
				};
			});

			queryClient.setQueryData(channelsQueryKey, (prev: Channel[] | undefined) => {
				if (!prev) return prev;
				return prev.map((c) =>
					c.id === Number(channelId)
						? {
								...c,
								unreadCount: 0,
								lastReadMessageId: data?.lastReadMessageId ?? c.lastReadMessageId ?? null,
								lastReadAt: data?.lastReadAt ?? new Date().toISOString(),
							}
						: c,
				);
			});
			},
		}),
	);

	const draftKey = channelId!;
	const existing = getChatDraft(draftKey);

	const [isDraggingFile, setIsDraggingFile] = useState(false);
	const [pendingAttachments, setPendingAttachments] = useState<PendingAttachment[]>(existing?.attachments ?? []);
	
	const [messageText, setMessageText] = useState(existing?.text ?? "");
	const [contextMenu, setContextMenu] = useState<{
		x: number;
		y: number;
		message: any;
	} | null>(null);
	const menuRef = useRef<HTMLDivElement | null>(null);

	type FullscreenMedia =
		| { kind: "image"; src: string; alt: string }
		| { kind: "video"; src: string; alt: string };
	const [fullscreenMedia, setFullscreenMedia] = useState<FullscreenMedia | null>(null);

	type DeletePopupValue = { open: boolean; message: any }
	const [deletePopup, setDeletePopup] = useState<DeletePopupValue | null>(null);

	type ActiveThreadValue = { threadId: number, message: any };
	const [activeThread, setActiveThread] = useState<ActiveThreadValue | null>(
		existing?.activeThreadMessage ? 
		{ threadId: existing.activeThreadMessage.id, message: existing.activeThreadMessage} : 
		null
	);
	const activeThreadId = activeThread?.threadId ?? null;

	type EditingMessageValue = { 
		originalText: string; 
		messageId: number;  
	}
	const [editingMessage, setEditingMessage] = useState<EditingMessageValue | null>(null);
	const [editDraft, setEditDraft] = useState("");

	const scrollContainerRef = useRef<HTMLDivElement | null>(null);
	const shouldAutoScrollRef = useRef(true);

	const messageListRef = useRef<HTMLDivElement | null>(null);
	const rafRef = useRef<number | null>(null);
	const saveScrollRafRef = useRef<number | null>(null);
	const hasSetInitialScrollRef = useRef(false);

	const isLoadingOlderRef = useRef(false);

    const isResizingRef = useRef(false);
    const [isResizing, setIsResizing] = useState(false);

	const fileInputRef = useRef<HTMLInputElement | null>(null);

	const scrollToBottom = () => {
		const el = scrollContainerRef.current;
		if (!el) return;

		el.scrollTop = el.scrollHeight;
	};

	const startResize = () => {
		isResizingRef.current = true;
		setIsResizing(true);
	};

	const endResize = () => {
		isResizingRef.current = false;
		setIsResizing(false);
	};

	const loadOlderPreserveScroll = async () => {
		const el = scrollContainerRef.current;
		if (!el || !hasNextPage || isFetchingNextPage) return;

		if (isLoadingOlderRef.current) return;
  		isLoadingOlderRef.current = true;

		const prevScrollHeight = el.scrollHeight;
	  
		await fetchNextPage();

		requestAnimationFrame(() => {
			const el2 = scrollContainerRef.current;
			if (!el2) return;
			const delta = el2.scrollHeight - prevScrollHeight;
			el2.scrollTop = el2.scrollTop + delta;

			isLoadingOlderRef.current = false;
		})
	}

	useEffect(() => {
		hasSetInitialScrollRef.current = false;
	}, [draftKey]);

	useEffect(() => {
		const scroll = getChatDraft(draftKey)?.scroll;

		setChatDraft(draftKey, {
			text: messageText,
			attachments: pendingAttachments,
			scroll: scroll,
			activeThreadMessage: activeThread?.message ?? undefined,
		});
	}, [draftKey, messageText, pendingAttachments, activeThreadId]);

	const prevLatestMessageId = prevLatestMessageIdRef.current;

	const latestIncomingMeta = useMemo(() => {
		const list = messagesData.messages ?? [];
		if (list.length === 0) {
			return { latestId: null, prevLatest: prevLatestMessageId, isNewFromOther: false };
		}

		const latest = list[list.length - 1];
		const latestId = latest?.id ?? null;
		const prevLatest = prevLatestMessageId;

		const isNewFromOther =
			typeof latestId === "number" &&
			typeof prevLatest === "number" &&
			latestId > prevLatest &&
			latest?.senderId !== session.user.id;

		return { latestId, prevLatest, isNewFromOther };
	}, [messagesData.messages, prevLatestMessageId, session.user.id]);

	const shouldSuppressFocusedNewDivider =
		latestIncomingMeta.isNewFromOther &&
		windowFocused &&
		typeof latestIncomingMeta.latestId === "number" &&
		typeof latestIncomingMeta.prevLatest === "number" &&
		dividerAnchorId != null &&
		dividerAnchorId >= latestIncomingMeta.prevLatest;

	useEffect(() => {
		const { latestId, prevLatest, isNewFromOther } = latestIncomingMeta;

		if (
			isNewFromOther &&
			typeof latestId === "number" &&
			typeof prevLatest === "number"
		) {
			if (shouldSuppressFocusedNewDivider) {
				setDividerAnchorId((prev) => {
					if (prev == null) return prev;
					if (prev < prevLatest) return prev;
					return latestId;
				});
				setHasPinnedNew(false, "window focused skip", { latestId, prevLatest });
			} else if (!windowFocused && !hasPinnedNewRef.current) {
				setDividerAnchorId((prev) => {
					if (prev != null && prev >= prevLatest) return prev;
					return prevLatest;
				});
				setHasPinnedNew(true, "auto pin latest", { latestId, prevLatest });
			}
		}

		if (typeof latestId === "number") {
			prevLatestMessageIdRef.current = latestId;
			// setHasPinnedNew(false, "latestId tracked", { latestId });
		}
	}, [
		latestIncomingMeta,
		shouldSuppressFocusedNewDivider,
		windowFocused,
	]);

	const markAsRead = async (advanceDivider: boolean) => {
		if (markChannelReadMutation.isPending) return;
		const latestId = messagesData.messages[messagesData.messages.length - 1]?.id ?? null;
		try {
			const res = await markChannelReadMutation.mutateAsync({
				channelId: Number(channelId),
			});
			if (advanceDivider) {
				const next = res?.lastReadMessageId ?? latestId ?? dividerAnchorId ?? null;
				setDividerAnchorId(next);
			}
		} catch (e) {
			console.error("Failed to mark channel read", e);
		}
	};

	useEffect(() => {
		if (!validChannel) return;
		if (!windowFocused) return;
		if (!isAtBottom) return;
		if ((messagesData?.messages?.length ?? 0) === 0) return;
		const latestId = messagesData.messages[messagesData.messages.length - 1]?.id;
		if (!latestId) return;
		if (dividerAnchorId != null && latestId <= dividerAnchorId) return;

		markAsRead(false);
	}, [
		validChannel,
		windowFocused,
		isAtBottom,
		messagesData.messages,
		dividerAnchorId,
		channelId,
	]);

	useLayoutEffect(() => {
		// if (isFetching) return;

		const el = scrollContainerRef.current;
		if (!el) return;
		
		if (hasSetInitialScrollRef.current) return;
	  
		if ((messagesData?.messages?.length ?? 0) === 0) return;

		const savedScroll = getChatDraft(draftKey)?.scroll;

		if (savedScroll?.mode === "bottom") {
			el.scrollTop = el.scrollHeight;
		} else if (savedScroll?.mode === "anchor") {
			const node = el.querySelector<HTMLElement>(
				`[data-message-id="${savedScroll.messageId}"]`
			);

			if (node) {
				const containerRect = el.getBoundingClientRect();
				const nodeRect = node.getBoundingClientRect();

				const nodeTopInContent = el.scrollTop + (nodeRect.top - containerRect.top);

				const targetScrollTop = nodeTopInContent - savedScroll.offsetPx;
				el.scrollTop = targetScrollTop;
			} else {
				if (typeof savedScroll.fallbackScrollTop === "number") {
				  	el.scrollTop = savedScroll.fallbackScrollTop;
				}
			}
		} else {
			el.scrollTop = el.scrollHeight;
		}
	
		hasSetInitialScrollRef.current = true;
	
	// update shouldAutoScroll based on where we are now
	const bottomThresholdPx = 80;
	const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
	const nearBottom = distanceFromBottom < bottomThresholdPx;
	shouldAutoScrollRef.current = nearBottom;
	setIsAtBottom(nearBottom);
}, [draftKey, isFetching, messagesData]);

	useEffect(() => {
		const el = scrollContainerRef.current;
		if (!el) return;

		const onScroll = () => {
			if (isResizingRef.current) return;

			const bottomThresholdPx = 80;
			const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
			const nearBottom = distanceFromBottom < bottomThresholdPx;
			shouldAutoScrollRef.current = nearBottom;
			setIsAtBottom(nearBottom);

			if (saveScrollRafRef.current == null) {
				saveScrollRafRef.current = requestAnimationFrame(() => {
					saveScrollRafRef.current = null;
				
					const current = getChatDraft(draftKey) ?? { text: "", attachments: [] };

					if (shouldAutoScrollRef.current) {
						setChatDraft(draftKey, {
							...current,
							scroll: { mode: "bottom" },
						});
						return;
					}

					const containerRect = el.getBoundingClientRect();
    				const nodes = el.querySelectorAll<HTMLElement>("[data-message-id]");

					let snapshot: { mode: "anchor"; messageId: string; offsetPx: number; fallbackScrollTop: number } | undefined;

					for (let i = 0; i < nodes.length; i++) {
						const node = nodes[i]!;
						const r = node.getBoundingClientRect();

						if (r.bottom > containerRect.top + 1) {
							const messageId = node.dataset.messageId;
							if (messageId) {
								snapshot = {
									mode: "anchor",
									messageId,
									offsetPx: r.top - containerRect.top,
									fallbackScrollTop: el.scrollTop,
								};
							}
							break;
						}
					}

					setChatDraft(draftKey, {
						...current,
						scroll: snapshot ?? current.scroll,
					});
				});
			}

			if (!hasNextPage || isFetchingNextPage) return;

			const maxScrollTop = el.scrollHeight - el.clientHeight;
			if (maxScrollTop <= 0) return;

			const progressFromTop = el.scrollTop / maxScrollTop;
			if (progressFromTop <= 0.3) {
				if (rafRef.current != null) return;
				if (isLoadingOlderRef.current) return;

				rafRef.current = requestAnimationFrame(async () => {
					await loadOlderPreserveScroll();
					rafRef.current = null;
				})
			}
		}

		el.addEventListener("scroll", onScroll, { passive: true });
		
		return () => el.removeEventListener("scroll", onScroll);
	}, [draftKey, hasNextPage, isFetchingNextPage, fetchNextPage]);

	useEffect(() => {
		const root = messageListRef.current;
		if (!root) return;
	  
		const ro = new ResizeObserver(() => {
			if (!hasSetInitialScrollRef.current) return;
			
			if (shouldAutoScrollRef.current) {
				scrollToBottom();
			}
		});
	  
		ro.observe(root);
		return () => ro.disconnect();
	}, []);

	useEffect(() => {
		const handleEscape = (e: KeyboardEvent) => {
			if (
				e.key === "Escape" &&
				(fullscreenMedia || deletePopup || editingMessage || activeThread)
			) {
				setFullscreenMedia(null);
				setDeletePopup(null);
				setEditingMessage(null);
				setActiveThread(null);
			}
		};

		const shouldLock = !!fullscreenMedia || !!deletePopup || !!editingMessage || !!activeThread;

		if (shouldLock) {
			document.addEventListener("keydown", handleEscape);
			document.body.style.overflow = "hidden";
		}

		return () => {
			document.removeEventListener("keydown", handleEscape);
			document.body.style.overflow = "";
		};
	}, [fullscreenMedia, deletePopup, editingMessage, activeThread]);

	const effectiveDividerAnchorId = useMemo(() => {
		if (shouldSuppressFocusedNewDivider) {
			return latestIncomingMeta.latestId ?? dividerAnchorId;
		}

		return dividerAnchorId;
	}, [shouldSuppressFocusedNewDivider, latestIncomingMeta.latestId, dividerAnchorId]);

	const firstUnreadMessageId = useMemo(() => {
		const messageList = messagesData.messages ?? [];
		if (messageList.length === 0) {
			return null;
		} else if (effectiveDividerAnchorId == null) {
			return null;
		} else {
			const firstUnread = messageList.find((m: any) => m.id > effectiveDividerAnchorId);
			if (firstUnread?.id === messageList[0].id) {
				return null;
			}
			const res = firstUnread?.id ?? null;
			return res;
		}
	}, [messagesData.messages, effectiveDividerAnchorId]);

	const uploadPendingFiles = async () => {
		const files = pendingAttachments.slice();
		setPendingAttachments([]);

		if (files.length === 0) return [];

		const presignRes = await createPresignedFileUploadMutation.mutateAsync({
			channelId: Number(channelId),
			files: files.map(a => ({
				fileName: a.file.name,
				contentType: a.file.type || "application/octet-stream",
				size: a.file.size,
			}))
		});

		await Promise.all(
			presignRes.uploads.map(async (u, idx) => {
				const file = files[idx]!.file;
				const putRes = await fetch(u.uploadUrl, {
					method: "PUT",
					headers: { "Content-Type": file.type || "application/octet-stream" },
					body: file,
				});
				if (!putRes.ok) {
					toast.error(`Upload failed (${putRes.status})`);
					throw new Error(`Upload failed (${putRes.status})`);
				}
			})
		)

		return presignRes.uploads.map(u => ({
			url: u.src,
			type: u.contentType,
			name: u.originalName,
			size: u.size,
		}));
	}

	// const handleSendTestMessage = () => {
	// 	if (!channelId) return;

	// 	createTestMessageMutation.mutate({
	// 		workspaceId: Number(workspaceId),
	// 		channelId: Number(channelId),
	// 	});
	// };

	const handleSend = async () => {
		if (!channelId) return;

		const trimmedStart = messageText.trim()
  		const validation = trimmedStart;
  
		const optimisticAttachments = pendingAttachments.map(a => ({
			url: a.previewUrl ?? undefined,        
			type: a.file.type,
			name: a.file.name,
			size: a.file.size,
		}));
		
		if (!validation && optimisticAttachments.length === 0) return;

		const tempId = crypto.randomUUID();
		const optimisticId = -Date.now();

		// Move divider before the optimistic message renders to avoid a jump.
			const latestExistingId =
				(messagesData.messages ?? []).reduce<number | null>((max, msg: any) => {
					if (typeof msg.id !== "number") return max;
					if (max == null) return msg.id;
					return Math.max(max, msg.id);
				}, dividerAnchorId);
			if (latestExistingId != null) {
				setDividerAnchorId(latestExistingId);
			}
			setHasPinnedNew(false, "handleSend reset");

		queryClient.setQueryData(messagesQueryKey, (prev: any) => {
			const optimisticMessage = {
				id: optimisticId,
				tempId,
				senderId: session.user.id,
  				sending: true,
				workspaceId: Number(workspaceId),
				channelId: Number(channelId),
				senderName: session?.user?.name || "You",
				text: trimmedStart,
				attachments: optimisticAttachments,
				createdAt: new Date().toISOString(),
				threadRootId: null,
			};

			if (!prev?.pages.length) {
				return {
					pages: [{ messages: [optimisticMessage], hasMore: false, nextCursor: undefined }],
					pageParams: [undefined],
				}
			}

			const pages = [...prev.pages];
  			const firstPage = pages[0] ?? { messages: [], hasMore: false, nextCursor: undefined };

			pages[0] = {
				...firstPage,
				messages: [...(firstPage.messages ?? []), optimisticMessage],
			};

			return { ...prev, pages };
		});

		shouldAutoScrollRef.current = true;

		let uploaded = [];
		try {
			setMessageText("");
			uploaded = await uploadPendingFiles();
		} catch (e) {
			toast.error("Failed to upload file(s)");
			return;
		}

		createMessageMutation.mutate(
			{
				workspaceId: Number(workspaceId),
				channelId: Number(channelId),
				text: trimmedStart,
				attachments: uploaded,
				tempId: tempId,
			},
		);
	};

	const handleEditCancel = () => {
		setEditingMessage(null);
		setEditDraft("");
	};

	const handleEditSave = async () => {
		if (!editingMessage) return;

		const trimmedStart = editDraft.trim();
  		const validation = trimmedStart;
		if (!validation || editDraft === editingMessage.originalText) {
			handleEditCancel();
			return;
		}
	  
		// Optimistic update
		queryClient.setQueryData(messagesQueryKey, (prev: any) => {
			if (!prev?.pages) return prev;

			const pages = prev.pages.map((p: any) => ({
				...p,
				messages: p.messages.map((m: any) =>
					m.id === editingMessage.messageId ? 
						{ 
							...m, 
							text: trimmedStart,
							editedAt: new Date().toISOString()
						} : m
				),
			}));

			return { ...prev, pages };
		});

		handleEditCancel();
	  
		updateMessageMutation.mutate(
		  	{ 
				messageId: editingMessage.messageId, 
				text: trimmedStart 
			},
		);
	};
	  
	const handleDelete = async (message: any) => {
		if (!channelId) return;

		queryClient.setQueryData(messagesQueryKey, (prev: any) => {
			if (!prev?.pages.length) return prev;

			const pages = prev.pages.map((page: any) => {
				return {
					...page,
					messages: (page.messages ?? []).filter((m: any) => m.id !== message.id),
				};
			});
			return { ...prev, pages };
		});

		deleteMessageMutation.mutate(
			{
				messageId: Number(message.id),
			},
		);
	};

	const handleMessageContextMenu = (e: React.MouseEvent, message: any) => {
		e.preventDefault();

		const MENU_WIDTH = 160;
		const MENU_HEIGHT = 160;
		const PADDING = 8; // padding from viewport edges

		const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();

		// Change: open menu to the left side of the trigger
		let x = rect.left - MENU_WIDTH;
		let y = rect.top;

		const vw = window.innerWidth;
		const vh = window.innerHeight;

		x = Math.max(PADDING, Math.min(rect.left, x));
		y = Math.min(vh - MENU_HEIGHT - PADDING, Math.max(PADDING, y));

		setContextMenu({ x, y, message });
	};

	useEffect(() => {
		if (!contextMenu) return;

		const handleMouseDown = (event: MouseEvent) => {
			if (menuRef.current?.contains(event.target as Node | null)) {
				return;
			}
			setContextMenu(null);
		};

		window.addEventListener("mousedown", handleMouseDown);

		return () => {
			window.removeEventListener("mousedown", handleMouseDown);
		};
	}, [contextMenu]);

	return (
		<div className="flex h-full flex-col bg-background">
			{/* Fullscreen Image Overlay */}
			{fullscreenMedia && (
				<div
					className="fixed inset-0 z-[100] flex items-center justify-center bg-black/90 backdrop-blur-sm"
					onClick={() => setFullscreenMedia(null)}
				>
					<button
						className="absolute top-4 right-4 z-[101] rounded-full bg-black/50 p-2 text-white transition-opacity hover:opacity-70"
						onClick={(e) => {
							e.stopPropagation();
							setFullscreenMedia(null);
						}}
						aria-label="Close"
					>
						<X className="h-6 w-6" />
					</button>
					{fullscreenMedia.kind === "image" ? (
						<img
							src={fullscreenMedia.src}
							alt={fullscreenMedia.alt}
							className="max-h-full max-w-full object-contain"
							onClick={(e) => e.stopPropagation()}
						/>
					) : (
						<video
							src={fullscreenMedia.src}
							className="max-h-full max-w-full object-contain"
							controls
							autoPlay
							playsInline
							onClick={(e) => e.stopPropagation()}
						/>
					)}
				</div>
			)}

			{deletePopup && (
				<div
					className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70"
					onClick={() => setDeletePopup(null)}
				>
					<div
						className="w-full max-w-lg max-h-[80vh] overflow-hidden rounded-2xl border border-border bg-card text-card-foreground shadow-xl flex flex-col"
						onClick={(e) => e.stopPropagation()}
						role="dialog"
						aria-modal="true"
						aria-label="Delete Message"
					>
						{/* Header */}
						<div className="flex items-start justify-between gap-4 p-6 pb-3 shrink-0">
							<div>
								<div className="text-xl font-bold">Delete Message</div>
								<div className="mt-1 text-sm text-muted-foreground">
									Are you sure you want to delete this message?
								</div>
							</div>

							<button
								type="button"
								className="rounded-md p-2 text-muted-foreground hover:bg-muted hover:text-foreground"
								onClick={() => setDeletePopup(null)}
								aria-label="Close"
							>
								<X className="h-5 w-5" />
							</button>
						</div>

						<div className="px-6 pb-4 mb-4 overflow-y-auto">
							<div className="rounded-xl bg-muted/30 p-4">
								<div className="flex items-start gap-3">
									<div className="h-10 w-10 rounded-full bg-primary/20" />
									<div className="min-w-0">
										<div className="flex items-baseline gap-2">
											<div className="font-semibold">
												{deletePopup.message?.senderName ?? "Unknown"}
											</div>
											<div className="text-xs text-muted-foreground">
												{deletePopup.message?.createdAt
													? new Date(deletePopup.message.createdAt).toLocaleString()
													: ""}
											</div>
										</div>

										{!!deletePopup.message?.text && (
											<div className="mt-1 break-words text-sm">
												{deletePopup.message.text}
											</div>
										)}

										{!!deletePopup.message?.attachments?.length && (
											<div className="mt-3 space-y-2">
												{deletePopup.message.attachments.map((a: any, idx: number) => (
													<div
														key={a.url ?? `att-${idx}`}
														className="flex items-center gap-3 rounded-lg border border-border bg-background/40 p-3"
													>
														<div className="flex h-10 w-10 items-center justify-center rounded-md bg-muted/40">
															<FileIcon className="h-6 w-6 text-muted-foreground" />
														</div>
														<div className="min-w-0">
															<div className="truncate text-sm">{a.name ?? "attachment"}</div>
															<div className="text-xs text-muted-foreground">
																{typeof a.size === "number"
																	? `${(a.size / 1024).toFixed(1)} KB`
																	: ""}
															</div>
														</div>
													</div>
												))}
											</div>
										)}
									</div>
								</div>
							</div>
						</div>

						{/* Footer */}
						<div className="flex items-center gap-3 p-6 pt-0 shrink-0">
							<button
								type="button"
								className="h-10 rounded-xl px-6 bg-muted hover:bg-muted/80 flex-1"
								onClick={() => setDeletePopup(null)}
							>
								Cancel
							</button>

							<button
								type="button"
								className="h-10 rounded-xl px-6 text-white bg-red-500 hover:bg-red-600/90 flex-1"
								onClick={() => {
									handleDelete(deletePopup.message);
									setDeletePopup(null);
								}}
							>
								Delete
							</button>
						</div>
					</div>
				</div>
			)}

			{showInitialLoading && 
				<div className="flex h-full items-center justify-center">
					<div className="h-8 w-8 animate-spin rounded-full border-4 border-border border-t-primary" />
				</div>
			}

			{!showInitialLoading && messagesData?.messages.length === 0 && 
				<div className="flex flex-1 items-center justify-center">
					<div className="rounded-lg border border-border bg-card p-6 text-center">
						<p className="text-muted-foreground">No messages.</p>
					</div>
				</div>
			}

			{/* Content Area */}
			<div className="flex flex-1 overflow-hidden">
				<ResizablePanelGroup
					direction="horizontal"
					autoSaveId={`${activeThreadId ?? channelId}-chat-panel`}
					className="flex-1"
				>
					<ResizablePanel
						defaultSize={activeThread ? 70 : 100}
						minSize={30}
					>
						<div className="flex h-full flex-col">
							{channel ? (
								<>
									{/* Messages Area */}
									<div 
										className={cn(
											"flex-1 overflow-x-hidden pb-4 pt-4 [overflow-anchor:none]",
											isResizing ? "overflow-y-hidden" : "overflow-y-auto"
										)}
										ref={scrollContainerRef}
									>
										<div ref={messageListRef}>
											{(() => {
												const grouped: Array<{
													key: string;
													senderName: string;
													createdAt: string;
													messages: any[];
												}> = [];
												const msgs = messagesData?.messages ?? [];
												for (let i = 0; i < msgs.length; i++) {
													const msg = msgs[i];
													const prev = grouped[grouped.length - 1];
													const msgDate = new Date(msg.createdAt);
													const minuteKey = `${msg.senderName || "Unknown"}-${msgDate.getFullYear()}-${msgDate.getMonth()}-${msgDate.getDate()}-${msgDate.getHours()}-${msgDate.getMinutes()}`;
													if (
														prev &&
														prev.senderName === (msg.senderName || "Unknown") &&
														prev.key === minuteKey
													) {
														prev.messages.push(msg);
													} else {
														grouped.push({
															key: minuteKey,
															senderName: msg.senderName || "Unknown",
															createdAt: msg.createdAt,
															messages: [msg],
														});
													}
												}
												return grouped.map((group, idx) => {
													let timeLabel = "";
													if (group.createdAt) {
														const date = new Date(group.createdAt);
														const now = new Date();
														const isToday = date.getFullYear() === now.getFullYear() &&
															date.getMonth() === now.getMonth() &&
															date.getDate() === now.getDate();

														if (isToday) {
															timeLabel = date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
														} else {
															timeLabel = date.toLocaleDateString() + " " + date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
														}
													}

													return (
														<div key={group.key + "-" + idx} className="flex">
															<div className="flex-1">
																{group.messages.filter((m) => m.threadRootId === null).map((msg, i) => {
																	const isFirst = i === 0;
																	const isSending = msg.sending ?? false;
																	const isMenuOpenForThisMsg = contextMenu?.message?.id === msg.id;
																	const replyCount = msg.replyCount;

																	return (
																		<React.Fragment key={msg.id}>
																			{firstUnreadMessageId === msg.id && (
																				<div className="mb-1 mt-1 mx-2 flex items-center text-xs gap-2 font-semibold text-primary">
																					<span className="h-px flex-1 bg-red-400" />
																					<span className="text-red-400">New</span>
																				</div>
																			)}

																			<div 
																				className={cn(
																					"group relative flex hover:bg-accent pl-4",
																					isFirst && "pt-1",
																					isMenuOpenForThisMsg && "bg-accent"
																				)}
																				data-message-id={String(msg.id)}
																			>
																				<div className="w-8 flex-shrink-0 pt-1">
																					{isFirst ? (
																						<div className="h-8 w-8 rounded-full bg-primary/20" />
																					) : (
																						<div className="w-8 opacity-0" />
																					)}
																				</div>
																			
																				<div className="min-w-0 flex-1 text-foreground pl-4">
																					{isFirst && (
																						<div className="flex items-baseline gap-2 pt-1">
																							<span className="font-semibold text-sm">{group.senderName}</span>
																							<span className="text-muted-foreground text-xs">{timeLabel}</span>
																						</div>
																					)}

																					{editingMessage?.messageId === msg.id ? (
																						<div className="relative mt-1 rounded-lg pb-2 pt-2">
																							<Textarea
																								value={editDraft}
																								onChange={(e) => setEditDraft(e.target.value)}
																								autoFocus
																								placeholder="Edit message..."
																								className="break-all"
																							/>
																							<div className="absolute bottom-4 right-4 flex gap-2">
																								<Button
																									variant="ghost"
																									size="sm"
																									onClick={handleEditCancel}
																									className="text-sm bg-primary-foreground"
																								>
																									Cancel
																								</Button>
																								<Button
																									size="sm"
																									onClick={handleEditSave}
																									disabled={updateMessageMutation.isPending}
																									className="text-sm"
																								>
																									Save
																								</Button>
																							</div>
																						</div>
																					) : (
																						<>
																							{!!msg.text && (
																								<p
																									className={cn(
																										"text-foreground whitespace-pre-wrap break-words pr-8",
																										msg.sending && "opacity-60 select-none"
																									)}
																								>
																									{linkifyText(msg.text, { onLinkClick: handleLinkClick })}
																									{!!msg.editedAt && (
																										<span className="ml-2 text-xs text-muted-foreground">(edited)</span>
																									)}
																								</p>
																							)}

																							{!!msg.attachments?.length && (() => {
																								const media = msg.attachments.filter((a: any) =>
																									a.type?.startsWith("image/") || a.type?.startsWith("video/")
																								);
																								const audio = msg.attachments.filter((a: any) => a.type?.startsWith("audio/"));
																								const file = msg.attachments.filter((a: any) => {
																									const t = a.type ?? "";
																									return (
																										!t.startsWith("image/") &&
																										!t.startsWith("video/") &&
																										!t.startsWith("audio/")
																									);
																								});

																								if (media.length === 0 && audio.length === 0 && file.length === 0) return null;

																								return (
																									<div className="mt-1 mb-1 space-y-2">
																										{media.length > 0 && (
																											<div className={cn(
																												"grid gap-2 justify-items-start", 
																												media.length === 1 ? "grid-cols-1" : "grid-cols-2",
																												media.length > 1 && "max-w-lg"
																											)}>
																												{media.map((a: any, idx: number) => (
																													<ChatMessageItem
																														key={a.url ?? `media-${idx}`}
																														channelId={Number(channelId)}
																														assetUrl={a.url ?? ""}
																														alt={a.name ?? (a.type?.startsWith("video/") ? "video" : "image")}
																														kind={a.type?.startsWith("video/") ? "video" : "image"}
																														variant={media.length === 1 ? "single" : "tile"}
																														onMediaClick={(src, alt, kind) => setFullscreenMedia({ src, alt, kind })}
																														isUploading={msg.sending ?? false}
																													/>
																												))}
																											</div>
																										)}

																										{audio.length > 0 && (
																											<div className="space-y-2">
																												{audio.map((a: any, idx: number) => (
																													<ChatAudioItem
																														key={a.url ?? `audio-${idx}`}
																														channelId={Number(channelId)}
																														assetUrl={a.url ?? ""}
																														name={a.name ?? "audio"}
																														isUploading={msg.sending ?? false}
																													/>
																												))}
																											</div>
																										)}

																										{file.length > 0 && (
																											<div className="space-y-2">
																												{file.map((a: any, idx: number) => (
																													<ChatFileItem
																														key={a.url ?? `file-${idx}`}
																														channelId={Number(channelId)}
																														assetUrl={a.url ?? ""}
																														name={a.name}
																														isUploading={msg.sending ?? false}
																													/>
																												))}
																											</div>
																										)}
																									</div>
																								);
																							})()}

																							{replyCount > 0 && (() => {
																								const lastReplyDate = new Date(msg.lastReplyAt);
																								const now = new Date();
																								const isToday = lastReplyDate.getFullYear() === now.getFullYear() &&
																									lastReplyDate.getMonth() === now.getMonth() &&
																									lastReplyDate.getDate() === now.getDate();

																								let lastReplyTimeLabel = "";
																								if (isToday) {
																									lastReplyTimeLabel = `today at ${lastReplyDate.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`;
																								} else {
																									const yesterday = new Date(now);
																									yesterday.setDate(yesterday.getDate() - 1);
																									const isYesterday = lastReplyDate.getFullYear() === yesterday.getFullYear() &&
																										lastReplyDate.getMonth() === yesterday.getMonth() &&
																										lastReplyDate.getDate() === yesterday.getDate();
																									
																									if (isYesterday) {
																										lastReplyTimeLabel = `yesterday at ${lastReplyDate.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`;
																									} else {
																										lastReplyTimeLabel = lastReplyDate.toLocaleDateString() + " at " + lastReplyDate.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
																									}
																								}

																								return (
																									<div
																										className="
																											flex items-center gap-2 mb-1 mt-1 max-w-[500px]
																											rounded-md px-2 py-1
																											border border-transparent
																											hover:border-border hover:bg-background/10
																											transition-colors
																											cursor-pointer
																											group/thread
																										"
																										onClick={(e) => {
																											e.stopPropagation();
																											setActiveThread({ threadId: msg.id, message: msg });
																										}}
																									>
																										<div className="h-6 w-6 rounded-full bg-primary/20 flex-shrink-0" />
																										<div
																											className="text-sm font-medium text-sky-500 hover:text-sky-400 hover:underline cursor-pointer"
																										>
																											{replyCount} {replyCount === 1 ? "reply" : "replies"}
																										</div>
																										<span className="text-xs text-muted-foreground hidden group-hover/thread:inline transition-opacity">
																											View thread
																										</span>
																										<span className="text-xs text-muted-foreground inline group-hover/thread:hidden transition-opacity">
																											Last reply {lastReplyTimeLabel}
																										</span>
																									</div>
																								);
																							})()}
																						</>
																					)}
																				</div>

																				{!msg.sending && (
																					<div
																						className={cn(
																							"absolute right-2 z-20",
																							isFirst ? "-top-4" : "-top-8",
																							"transition-opacity",
																							(isMenuOpenForThisMsg)
																								? "opacity-100 pointer-events-auto"
																								: "opacity-0 pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto"
																						)}
																						onClick={(e) => e.stopPropagation()}
																					>
																						<div className="flex items-center gap-1 rounded-xl border bg-background/95 backdrop-blur px-1 py-1 shadow-sm">
																							<button
																								type="button"
																								aria-label="Copy"
																								className="cursor-pointer inline-flex h-8 w-8 items-center justify-center rounded-lg text-muted-foreground hover:bg-muted hover:text-foreground"
																								onClick={(e) => {
																									e.stopPropagation()
																									navigator.clipboard.writeText(msg.text);
																								}}
																							>
																								<Copy className="h-4 w-4" />	
																							</button>

																							<button
																								type="button"
																								aria-label="Reply to thread"
																								className="cursor-pointer inline-flex h-8 w-8 items-center justify-center rounded-lg text-muted-foreground hover:bg-muted hover:text-foreground"
																								onClick={(e) => {
																									e.stopPropagation()
																									setActiveThread({ threadId: msg.id, message: msg });
																								}}
																							>
																								<Reply className="h-4 w-4" />	
																							</button>

																							{msg.senderId === session.user.id && (
																								<button
																									type="button"
																									aria-label="Edit"
																									className="cursor-pointer inline-flex h-8 w-8 items-center justify-center rounded-lg text-muted-foreground hover:bg-muted hover:text-foreground"
																									onClick={(e) => {
																										e.stopPropagation()
																										setEditingMessage({ originalText: msg.text, messageId: msg.id })
																										setEditDraft(msg.text);
																									}}
																								>
																									<Pencil className="h-4 w-4" />	
																								</button>
																							)}


																							<button
																								type="button"
																								aria-label="More actions"
																								className="cursor-pointer inline-flex h-8 w-8 items-center justify-center rounded-lg text-muted-foreground hover:bg-muted hover:text-foreground"
																								onClick={(e) => {
																									e.stopPropagation();
																									handleMessageContextMenu(e, msg);
																								}}
																							>
																								<MoreVertical className="h-4 w-4" />
																							</button>
																						</div>
																					</div>
																				)}
																			</div>
																		</React.Fragment>
																	);
																})}
															</div>
														</div>
													);
												});
											})()}
										</div>
									</div>

									{/* Message Input Area */}
									<div className="border-t border-border bg-background p-4">
										<div className="mx-auto max-w-4xl">
											{/* {import.meta.env.DEV && (
												<div className="mb-2 flex justify-end">
													<Button
														variant="outline"
														size="sm"
														onClick={handleSendTestMessage}
														disabled={!channelId || createTestMessageMutation.isPending}
													>
														Send Test Message
													</Button>
												</div>
											)} */}
											{pendingAttachments.length > 0 && (
												<div className="mb-3 flex flex-wrap gap-2">
													{pendingAttachments.map((a) => (
														<div
															key={a.id}
															className="flex items-center gap-2 rounded-md border border-border bg-card px-2 py-2"
														>
															{a.file.type.startsWith("image/") ? (
																<img
																	src={a.previewUrl}
																	alt={a.file.name}
																	className="h-10 w-10 rounded object-cover"
																/>
															) : a.file.type.startsWith("video/") ? (
																<video
																	src={a.previewUrl}
																	className="h-10 w-10 rounded object-cover"
																	muted
																	playsInline
																	preload="metadata"
																/>
															) : a.file.type.startsWith("audio/") ? (
																<Volume2 className="h-10 w-10 text-muted-foreground" />
															) : (
																<FileIcon className="h-10 w-10 text-muted-foreground" />
															)}

															{!a.file.type.startsWith("image/") && !a.file.type.startsWith("video/") && (
																<div className="min-w-0">
																	<div className="max-w-[100px] truncate text-sm">{a.file.name}</div>
																</div>
															)}

															<Button
																variant="ghost"
																size="icon"
																className="h-6 w-6"
																onClick={() => {
																	setPendingAttachments((prev) => {
																		const found = prev.find((x) => x.id === a.id);
																		if (found?.previewUrl) URL.revokeObjectURL(found.previewUrl);
																		return prev.filter((x) => x.id !== a.id);
																	});
																}}
															>
																<X className="h-4 w-4" />
															</Button>
														</div>
													))}
												</div>
											)}
											<div className="flex gap-2">
												<Button
													variant="ghost"
													size="icon"
													className="flex-shrink-0 text-muted-foreground hover:text-foreground"
													onClick={() => {
														fileInputRef.current?.click()
													}}
												>
													<Plus className="h-5 w-5" />
												</Button>

												<input
													ref={fileInputRef}
													type="file"
													hidden
													multiple
													onChange={(e) => {
														const input = e.currentTarget;
														const files = Array.from(input.files ?? []);
														if (!files.length) return;

														setPendingAttachments(prev =>
															prev.concat(
																files.map(file => {
																	const mime = file.type || mimeFromExt(file.name) || "application/octet-stream";
																	const blobUrl = URL.createObjectURL(file);
												
																	return {
																		id: crypto.randomUUID(),
																		file,
																		type: mime,  
																		previewUrl: blobUrl
																	}
																})
															)
														);

														input.value = "";
													}}
												/>
												<div
													className={cn(
														"flex-1 rounded-md transition",
														isDraggingFile && "ring-2 ring-primary ring-inset bg-primary/5"
													)}
													onDragEnter={() => setIsDraggingFile(true)}
													onDragLeave={() => setIsDraggingFile(false)}
													onDrop={(e) => {
														handleDropInput({
															e,
															setDraggingFile: setIsDraggingFile,
															setPendingAttachments
														})
													}}
													onPaste={(e) => {
														handlePasteAttachments({e, setDraggingFile: setIsDraggingFile, setPendingAttachments})
													}}
												>
													<Textarea
														placeholder={`Message #${channel.title}`}
														className="w-full max-h-40 overflow-y-auto overflow-x-hidden whitespace-pre-wrap break-words"
														value={messageText}
														onChange={(e) => setMessageText(e.target.value)}
														onKeyDown={(e) => {
															if (e.key === "Enter" && !e.shiftKey) {
																e.preventDefault();
																handleSend();
															}
														}}
													/>
												</div>
												<Button
													variant="ghost"
													size="icon"
													className="flex-shrink-0 text-muted-foreground hover:text-foreground"
												>
													<Smile className="h-5 w-5" />
												</Button>
												<Button 
													className="flex-shrink-0" 
													onClick={() => handleSend()}
													disabled={
														createMessageMutation.isPending || 
														(!messageText.trim() && pendingAttachments.length === 0)
													}
												>
													<Send className="h-4 w-4" />
												</Button>
											</div>
										</div>
									</div>
								</>
							) : (
								<div className="flex flex-1 items-center justify-center">
									<div className="rounded-lg border border-border bg-card p-6 text-center">
										<p className="text-muted-foreground">No channel selected</p>
									</div>
								</div>
							)}
						</div>
					</ResizablePanel>
	
					
					{/* Thread Section */}
					{activeThread && (
						<>
							<ResizableHandle 
								onPointerDown={startResize}
								onPointerUp={endResize}
							/>
							<ResizablePanel
								defaultSize={30}
								minSize={30}
							>
								<ChatThread 
									channelId={Number(channelId)}
									workspaceId={Number(workspaceId)}
									session={session}
									activeThreadId={activeThreadId!}
									activeThreadMessage={activeThread.message}
									onClose={() => setActiveThread(null)}
  									onMediaClick={(src, alt, kind) => setFullscreenMedia({ src, alt, kind })}
								/>
							</ResizablePanel>
						</>
					)}
				</ResizablePanelGroup>
			</div>
		

			{contextMenu && (
				<div
					className="fixed z-50 w-40 rounded-md border bg-popover text-popover-foreground shadow-lg py-1"
					style={{ top: contextMenu.y, left: contextMenu.x }}
					ref={menuRef}
					onClick={(e) => e.stopPropagation()}
				>
					<button
						type="button"
						className="w-full px-3 py-1.5 text-left text-sm hover:bg-accent hover:text-accent-foreground"
						onClick={() => {
							setContextMenu(null);
							setActiveThread({ threadId: contextMenu.message.id, message: contextMenu.message });
						}}
					>
						Reply
					</button>

					
					{contextMenu?.message?.senderId === session.user.id && (
						<>
							<button
								type="button"
								className="w-full px-3 py-1.5 text-left text-sm hover:bg-accent hover:text-accent-foreground"
								onClick={() => {
									// closeTabs(tabs)
									setContextMenu(null);
									setEditingMessage({ originalText: contextMenu.message.text, messageId: contextMenu.message.id })
									setEditDraft(contextMenu.message.text);
								}}
							>
								Edit
							</button>
							<button
								type="button"
								className="w-full px-3 py-1.5 text-left text-sm text-red-400 hover:bg-red-500/10"
								onClick={() => {
									setContextMenu(null);
									setDeletePopup({ open: true, message: contextMenu.message })
								}}
							>
								Delete
							</button>
						</>
					)}
				</div>
			)}
		</div>
	);
};
